import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { usePostalCodeContext } from '../../../contexts/PostalCodeProvider';
import usePostalCodesActions from '../../../hooks/APIHooks/users/usePostalCodesActions';
import { PostalCode } from '../../../types/postalCodes';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import PostalCodeDetailsModal from '../../CommonComps/Modals/PostalCodeDetailsModal';

const PostalCodeModalsContainer = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    targetedPostalCode,
    showDeleteModal,
    showViewModal,
    setShowDeleteModal,
    setShowViewModal,
  } = usePostalCodeContext();

  // integration of custom API hooks here
  const { deletePostalCodeByIdHandler } = usePostalCodesActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete postal code feature declared here
  const deletePostalCodeHandler = async () => {
    try {
      setIsDeleting(true);

      await deletePostalCodeByIdHandler
        .mutateAsync({ postalCodeId: targetedPostalCode?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Postal Code Deleted!!');

            setShowDeleteModal(false);
            queryClient.invalidateQueries({ queryKey: ['postal-code-list'] });
          } else {
            toast.error(res.data.message || 'Postal Code Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering postal code modals container section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showViewModal && (
          <PostalCodeDetailsModal
            postalCode={targetedPostalCode as PostalCode}
            handleModalClose={() => setShowViewModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this city's{' '}
                <span className='font-semibold text-base-light-blue'>
                  ({targetedPostalCode?.city_name})
                </span>{' '}
                postal codes? Once you delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deletePostalCodeHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default PostalCodeModalsContainer;
